import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, Input, Select, Avatar, Typography } from "antd";
import {
  UploadOutlined,
  UserOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useLocations } from "../../hooks/useLocation";
import { uploadImageToCloudinary } from "../../helpers/cloudinary";
import { Vendor } from "../../hooks/useVendors";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { vendorSchema } from "../../validations/vendorSchema";

const { Option } = Select;
const { Title, Text } = Typography;

interface AddVendorModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  vendor?: Vendor | null;
  canSeeAllLocations: boolean;
  userLocationId?: number;
}

type VendorFormData = {
  name: string;
  email: string;
  phone: string;
  location_id: string;
};

const AddVendorModal: React.FC<AddVendorModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  vendor,
  canSeeAllLocations,
  userLocationId,
}) => {
  const { data: locations } = useLocations();
  const [avatar, setAvatar] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: {
      name: vendor?.name || "",
      email: vendor?.email || "",
      phone: vendor?.phone || "",
      location_id: vendor ? vendor.location_id.toString() : "",
    },
  });

  // Reset form and avatar when modal is opened or vendor prop changes
  useEffect(() => {
    if (visible) {
      if (vendor) {
        reset({
          name: vendor.name,
          email: vendor.email,
          phone: vendor.phone,
          location_id: vendor.location_id ? vendor.location_id.toString() : "",
        });
        setAvatar(vendor.avatar);
      } else {
        // New vendor - set default location based on permissions (can be empty)
        const defaultLocationId = canSeeAllLocations
          ? ""
          : userLocationId
          ? userLocationId.toString()
          : "";

        reset({
          name: "",
          email: "",
          phone: "",
          location_id: defaultLocationId,
        });
        setAvatar(null);
      }
    }
  }, [visible, vendor, reset, canSeeAllLocations, userLocationId]);

  const onFormSubmit = (data: VendorFormData) => {
    // Handle location_id properly - can be empty for users with permission
    let finalLocationId = null;

    if (canSeeAllLocations) {
      // User can see all locations - use their selection (can be empty)
      finalLocationId = data.location_id
        ? parseInt(data.location_id, 10)
        : null;
    } else {
      // Restricted user - use their location or null if not available
      finalLocationId = userLocationId || null;
    }

    const submissionData = {
      ...data,
      avatar,
      location_id: finalLocationId,
    };

    onSubmit(submissionData);
    reset();
    setAvatar(null);
    onCancel();
  };

  const handleAvatarUpload = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadImageToCloudinary(file);
      setAvatar(url);
    } catch (error) {
      console.error("Failed to upload avatar:", error);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleRemoveAvatar = () => {
    setAvatar(null);
  };

  const availableLocations = canSeeAllLocations
    ? locations
    : locations?.filter((loc) => loc.id === userLocationId);

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <UserOutlined className="text-blue-600" />
          <Title level={4} className="mb-0">
            {vendor ? "Edit Vendor" : "Add New Vendor"}
          </Title>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
      className="vendor-modal"
    >
      <div className="pt-4">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Logo Section */}
          <div className="flex flex-col items-center space-y-4 p-6 bg-gray-50 rounded-lg">
            <div className="relative">
              <Avatar
                size={80}
                src={avatar}
                icon={<UserOutlined />}
                className="border-4 border-white shadow-lg"
              />
              {avatar && (
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveAvatar}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  style={{ minWidth: "auto", width: 24, height: 24 }}
                />
              )}
            </div>

            <Upload
              beforeUpload={handleAvatarUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                className="border-dashed"
              >
                {avatar ? "Change Logo" : "Upload Logo"}
              </Button>
            </Upload>
            <Text type="secondary" className="text-xs">
              Optional: Square image, max 2MB
            </Text>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field - Required */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor Name <span className="text-red-500">*</span>
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    placeholder="Enter vendor name"
                    status={errors.name ? "error" : ""}
                    className="rounded-lg"
                  />
                )}
              />
              {errors.name && (
                <Text type="danger" className="text-sm">
                  {errors.name.message as string}
                </Text>
              )}
            </div>

            {/* Email Field - Optional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    size="large"
                    placeholder="vendor@example.com (optional)"
                    status={errors.email ? "error" : ""}
                    className="rounded-lg"
                  />
                )}
              />
              {errors.email && (
                <Text type="danger" className="text-sm">
                  {errors.email.message as string}
                </Text>
              )}
            </div>

            {/* Phone Field - Optional */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    placeholder="+1234567890 (optional)"
                    status={errors.phone ? "error" : ""}
                    className="rounded-lg"
                  />
                )}
              />
              {errors.phone && (
                <Text type="danger" className="text-sm">
                  {errors.phone.message as string}
                </Text>
              )}
            </div>

            {/* Location Field - Optional */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <Controller
                name="location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="large"
                    placeholder={
                      canSeeAllLocations
                        ? "Select a location (optional)"
                        : "Your location"
                    }
                    disabled={!canSeeAllLocations}
                    allowClear={canSeeAllLocations}
                    status={errors.location_id ? "error" : ""}
                    className="w-full"
                    style={{ borderRadius: "8px" }}
                  >
                    {availableLocations?.map((location) => (
                      <Option key={location.id} value={location.id.toString()}>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span>{location.name}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.location_id && (
                <Text type="danger" className="text-sm">
                  {errors.location_id.message as string}
                </Text>
              )}
              {!canSeeAllLocations && userLocationId && (
                <Text type="secondary" className="text-xs mt-1">
                  Location is set to your assigned location
                </Text>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button size="large" onClick={onCancel} className="px-6">
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={isSubmitting}
              className="px-6 bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
            >
              {vendor ? "Update Vendor" : "Create Vendor"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddVendorModal;
